import { Link } from 'react-router-dom';
import AnimatedTagline from '../components/AnimatedTagline';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Home = () => {
  const [whatWeDoRef, whatWeDoVisible] = useScrollAnimation();
  const [whyDifferentRef, whyDifferentVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div>
      {/* Hero Section with "Embrace Change" */}
      <section id="home" className="min-h-[80vh] flex items-center">
        <div className="grid grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-16 items-center w-full">
          {/* Left: Animated Tagline */}
          <div className="text-left">
            <AnimatedTagline />
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl max-w-lg mb-4 sm:mb-6 lg:mb-8 font-light">
              <span className="text-gray-500 dark:text-gray-400">Transform </span>
              <span className="text-glam-primary dark:text-white font-medium">documents, media, data, and cultural collections</span>
              <span className="text-gray-500 dark:text-gray-400"> into </span>
              <span className="text-glam-primary dark:text-white font-medium">intelligent, accessible digital experiences</span>
              <span className="text-gray-500 dark:text-gray-400">.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4">
              <Link
                to="/services"
                className="px-3 py-2 sm:px-4 sm:py-3 lg:px-8 lg:py-4 bg-glam-primary text-white font-medium hover:bg-glam-secondary transition-colors text-xs sm:text-sm lg:text-base"
              >
                Our Approach →
              </Link>
              <Link
                to="/case-studies"
                className="px-3 py-2 sm:px-4 sm:py-3 lg:px-8 lg:py-4 border border-glam-primary text-glam-primary hover:bg-glam-primary hover:text-white transition-colors text-xs sm:text-sm lg:text-base"
              >
                See Our Process
              </Link>
            </div>
          </div>
          
          {/* Right: Visual Collage - Always on right, scales down */}
          <div className="relative">
            <div className="aspect-square relative w-full max-w-[200px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-none mx-auto">
              {/* Laptop funnel papers - back left */}
              <img
                src="/images/laptop-funnel-papers-2.png"
                alt="Laptop with funnel and papers representing digital transformation"
                className="absolute -top-1 -left-1 sm:-top-2 sm:-left-2 lg:-top-4 lg:-left-4 w-3/5 h-3/5 object-cover rounded-lg sm:rounded-2xl lg:rounded-[3rem] shadow-lg z-10"
              />
              {/* Museum robot - center right */}
              <img
                src="/images/museum-robot-1.png"
                alt="Robot in museum setting representing AI in cultural institutions"
                className="absolute top-1/4 -right-1 sm:top-1/4 sm:-right-2 lg:top-1/3 lg:-right-4 w-3/5 h-3/5 object-cover rounded-lg sm:rounded-2xl lg:rounded-[3rem] shadow-lg z-20"
              />
              {/* Pedestal papers - bottom left */}
              <img
                src="/images/pedestal-papers-1.png"
                alt="Papers on pedestal representing cultural collections"
                className="absolute bottom-[-15%] left-[10%] sm:bottom-[-20%] lg:bottom-[-30%] w-3/5 h-3/5 object-cover rounded-lg sm:rounded-2xl lg:rounded-[3rem] shadow-lg z-30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section 
        id="what-we-do" 
        ref={whatWeDoRef}
        className={`py-20 transition-all duration-1000 ${
          whatWeDoVisible 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-0 transform translate-y-8'
        }`}
      >
        <div className="max-w-3xl mb-20">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            We help cultural institutions <br />
            <span className="italic text-glam-secondary dark:text-white">thrive in the digital age</span>
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <Link to="/services#consulting" className="block p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold mb-3">Consulting</h3>
              <p className="text-glam-secondary dark:text-gray-300 text-base">
                Strategic guidance to help cultural institutions navigate digital transformation and technology decisions.
              </p>
            </Link>
            <Link to="/services#software-design" className="block p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-lg transition-shadow ml-8">
              <h3 className="text-xl font-semibold mb-3">Software Design</h3>
              <p className="text-glam-secondary dark:text-gray-300 text-base">
                User-centered design of digital platforms that serve both institutional needs and public engagement.
              </p>
            </Link>
          </div>
          
          <div className="space-y-8 md:mt-12">
            <Link to="/services#custom-development" className="block p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-lg transition-shadow mr-8">
              <h3 className="text-xl font-semibold mb-3">Custom Development</h3>
              <p className="text-glam-secondary dark:text-gray-300 text-base">
                Full-scale development of tailored software solutions for cultural heritage institutions.
              </p>
            </Link>
            <Link to="/services#training" className="block p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold mb-3">Training</h3>
              <p className="text-glam-secondary dark:text-gray-300 text-base">
                Comprehensive training programs to help staff maximize the value of their digital systems.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section 
        id="why-different" 
        ref={whyDifferentRef}
        className={`py-20 bg-gray-50 dark:bg-gray-900 -mx-4 px-4 transition-all duration-1000 ${
          whyDifferentVisible 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-0 transform translate-y-8'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Technology that serves <br />
              <span className="italic">culture, not the other way around</span>
            </h2>
            <p className="text-lg text-glam-secondary dark:text-gray-300 mb-8">
              Flexible technology that amplifies your mission.
            </p>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold mb-8">See what we've done</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link to="/case-studies" className="block p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-lg transition-shadow">
                <h4 className="text-xl font-semibold mb-3">AI Powered Access to NYC History</h4>
                <p className="text-glam-secondary dark:text-gray-300 text-base">
                  Transformed thousands of NYC history documents into an intelligent, searchable platform
                </p>
              </Link>
              <div className="p-6 bg-gray-50 dark:bg-gray-900">
                <h4 className="text-xl font-semibold mb-3 text-gray-500">Be The Next Success Story</h4>
                <p className="text-glam-secondary dark:text-gray-300 text-base">
                  We're working on exciting projects with institutions worldwide — yours could be next
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        id="get-started" 
        ref={ctaRef}
        className={`py-20 transition-all duration-1000 ${
          ctaVisible 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-0 transform translate-y-8'
        }`}
      >
        <div className="max-w-3xl ml-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Learn More
          </h2>
          <p className="text-xl text-glam-secondary dark:text-gray-300 mb-8 max-w-xl">
            Let's explore how we can help your institution thrive in the digital future.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-glam-primary text-white font-medium hover:bg-glam-secondary transition-colors"
          >
            Start the Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;